import type { ReactNode } from 'react';
import React from 'react';
import {
    Grid,
    Subheader,
    Button,
    useStyles,
    pxToRem,
} from '@archipro-design/aria';
import * as S from './Details.style';
import { DownloadLine, EditorLink } from '@archipro-design/icons';
import { useSaveToDesignBoard } from '~/modules/design-board/hook/use-save-to-design-board';
import SaveToDesignBoard from '@modules/design-board/component/SaveToDesignBoard';
import { getDetailsItems } from './util/get-details-items';
import { Link } from '@remix-run/react';
import { DOWNLOAD_ATTR_FILE_TITLE, DOWNLOAD_ATTR_PROFESSIONAL_TITLE, TRACK_DOWNLOAD_CLASSNAME, useTracker } from '@archipro-website/tracker';
import type { ProjectPageLoader } from '@modules/project/type';
import { useUser } from '~/modules/user';
import { hasFeature } from '~/modules/root';

import cx from 'classnames';

interface DetailsProps {
    data: ProjectPageLoader['project'];
    isEditPage?: boolean;
}

const MAX_LIMITED_LINES = 4;

const Details = ({ data, isEditPage }: DetailsProps) => {
    const { css } = useStyles();
    const user = useUser();
    const hasProjectAddressFeature = hasFeature(
        user.FeatureFlags,
        'feature_project_address'
    );
    const { favouriteItem } = useSaveToDesignBoard(
        'ProfessionalPin',
        data.Professional.ID
    );

    const tracker = useTracker();

    const [showingAll, setShowingAll] = React.useState(false);

    let index = 1; // Start at 1 to cater for the first professional line

    const detailsItems = getDetailsItems(data, hasProjectAddressFeature);

    const showItem = (
        label: string,
        value: string,
        link?: string,
        isExternal?: boolean
    ): ReactNode => {
        if (!showingAll && ++index > MAX_LIMITED_LINES) {
            return null;
        }
        const final = value.replace(/(m)([23])/i, '$1<sup>$2</sup>');
        return (
            <React.Fragment key={`${label}_${value}`}>
                <div />
                <Subheader className={css(S.ListItemLabel)} variant="01">
                    {`${label} `}
                </Subheader>
                {link ? (
                    <a
                        className={cx(
                            css(S.LinkListItem),
                            TRACK_DOWNLOAD_CLASSNAME
                        )}
                        href={link}
                        download={label && !isExternal}
                        target={isExternal ? '_blank' : undefined}
                        {
                            ...{
                                [DOWNLOAD_ATTR_PROFESSIONAL_TITLE]: data.Professional.Title,
                                [DOWNLOAD_ATTR_FILE_TITLE]: value,
                            }
                        }
                        rel="noopener"
                    >
                        <div>
                            {isExternal ? (
                                <EditorLink
                                    variables={{
                                        '1xSize': pxToRem(18),
                                    }}
                                />
                            ) : (
                                <DownloadLine />
                            )}
                        </div>
                        <div
                            className={css(S.ListItemContent)}
                            dangerouslySetInnerHTML={{ __html: final }}
                        />
                    </a>
                ) : (
                    <div className={css(S.ListItem)}>
                        <div
                            className={css(S.ListItemContent)}
                            dangerouslySetInnerHTML={{ __html: final }}
                        />
                    </div>
                )}
            </React.Fragment>
        );
    };

    return (
        <div className={css(S.DetailsContainer)}>
            {detailsItems.length ? (
                <>
                    <Grid
                        className={css(S.Grid)}
                        columns={`${pxToRem(924)} ${pxToRem(368)} 1fr`}
                    >
                        <div />
                        <Subheader
                            className={css(S.ListItemLabel)}
                            variant="01"
                        >
                            Project by
                        </Subheader>
                        <Grid
                            columns={`1fr ${pxToRem(227)} 0 ${pxToRem(74)}`}
                            className={css(S.ListItem)}
                        >
                            <div className={css(S.ProfessionalContent)}>
                                {isEditPage ? (
                                    data.Professional.Title
                                ) : (
                                    <Link
                                        to={data.Professional.Link}
                                        onClick={() => {
                                            tracker.log('ProjectProfileClick', {
                                                url: new URL(
                                                    window.location.href
                                                ),
                                                targetTracker:
                                                    'archiproTracker',
                                            });
                                        }}
                                        prefetch="intent"
                                    >
                                        {data.Professional.Title}
                                    </Link>
                                )}
                            </div>
                            <SaveToDesignBoard
                                {...favouriteItem}
                                heartButton={{
                                    className: css(S.LikeButton),
                                    size: 36,
                                    variables: {
                                        buttonPadding: 0,
                                    },
                                    disabled: isEditPage,
                                }}
                                disable={isEditPage}
                            />
                        </Grid>

                        {detailsItems.map((item) => {
                            if (Array.isArray(item.value)) {
                                return item.value.map((val, i) =>
                                    showItem(
                                        i === 0 ? item.itemName : '',
                                        val.Title,
                                        val.InternalLink || val.ExternalLink,
                                        !!val.ExternalLink
                                    )
                                );
                            }

                            return showItem(
                                item.itemName,
                                item.value,
                                item.link,
                                item.isExternal
                            );
                        })}
                    </Grid>
                    {detailsItems.length > MAX_LIMITED_LINES - 1 && (
                        <Grid columns={`${pxToRem(924)} ${pxToRem(368)} 1fr`}>
                            <div />
                            <Button
                                onClick={() => setShowingAll(!showingAll)}
                                size={16}
                                transparent
                                variables={{
                                    buttonPadding: 0,
                                }}
                                className={css(
                                    showingAll
                                        ? S.ShowLessButton
                                        : S.ShowMoreButton
                                )}
                            >
                                {`Show ${
                                    showingAll ? 'less' : 'more'
                                } project details`}
                            </Button>
                        </Grid>
                    )}
                </>
            ) : null}
        </div>
    );
};

export default Details;
